/** CPEs panel — display CPE 2.3 URIs grouped by vendor → product → versions.
 *
 * A CPE 2.3 URI looks like:
 *   cpe:2.3:a:apache:log4j:2.14.1:*:*:*:*:*:*:*
 *
 * Fields (0-indexed after the "cpe:2.3:" prefix):
 *   [0] part        (a=application, o=os, h=hardware)
 *   [1] vendor
 *   [2] product
 *   [3] version
 *   [4] update
 *   [5] edition
 *   [6] language
 *   [7] sw_edition
 *   [8] target_sw
 *   [9] target_hw
 *   [10] other
 */
import { useState } from "react";

const PART_LABELS = { a: "Application", o: "Operating System", h: "Hardware" };

function parseCpe(uri) {
  if (!uri) return null;
  let parts;
  if (uri.startsWith("cpe:2.3:")) {
    // CPE 2.3 → cpe:2.3:{part}:{vendor}:{product}:{version}:{update}:{edition}:{lang}:{sw_edition}:{target_sw}:{target_hw}:{other}
    parts = uri.slice("cpe:2.3:".length).split(":");
  } else if (uri.startsWith("cpe:/")) {
    // CPE 2.2 → cpe:/{part}:{vendor}:{product}:{version}:{update}:{edition}:{language}
    parts = uri.slice("cpe:/".length).split(":");
  } else {
    return null;
  }
  while (parts.length < 11) parts.push("*");
  return {
    uri,
    part: parts[0] || "*",
    vendor: parts[1] || "*",
    product: parts[2] || "*",
    version: parts[3] || "*",
    update: parts[4] || "*",
    edition: parts[5] || "*",
    target_sw: parts[8] || "*",
    target_hw: parts[9] || "*",
  };
}

function _displayVersion(v) {
  if (!v || v === "*") return "any";
  if (v === "-") return "n/a";
  return v;
}

function _human(s) {
  if (!s || s === "*" || s === "-") return s;
  return s.replace(/_/g, " ");
}

function CopyLink({ uri }) {
  const [copied, setCopied] = useState(false);

  const onClick = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(uri);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{
        fontFamily: "monospace",
        fontSize: 11,
        color: "var(--text-muted)",
        whiteSpace: "nowrap",
      }}>
        {uri}
      </span>
      <span
        onClick={onClick}
        style={{
          cursor: "copy",
          color: copied ? "var(--severity-low)" : "var(--accent)",
          fontSize: 11,
          fontFamily: "monospace",
        }}
      >
        {copied ? "copied" : "copy"}
      </span>
    </span>
  );
}

function CpeTable({ list, suggested }) {
  // Group by part → vendor → product
  const grouped = {};
  for (const p of list) {
    const partKey = PART_LABELS[p.part] || p.part;
    grouped[partKey] = grouped[partKey] || {};
    grouped[partKey][p.vendor] = grouped[partKey][p.vendor] || {};
    grouped[partKey][p.vendor][p.product] = grouped[partKey][p.vendor][p.product] || [];
    grouped[partKey][p.vendor][p.product].push(p);
  }
  return (
    <>
      {Object.entries(grouped).map(([partLabel, vendors]) => (
        <div key={partLabel} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
            {partLabel}
          </div>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)", color: "var(--text-muted)" }}>
                <th style={{ textAlign: "left", padding: "6px 10px" }}>Vendor</th>
                <th style={{ textAlign: "left", padding: "6px 10px" }}>Product</th>
                <th style={{ textAlign: "left", padding: "6px 10px" }}>Version</th>
                <th style={{ textAlign: "left", padding: "6px 10px" }}>Target</th>
                <th style={{ textAlign: "left", padding: "6px 10px" }}>URI</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(vendors).map(([vendor, products]) =>
                Object.entries(products).map(([product, entries]) =>
                  entries.map((p, i) => (
                    <tr key={p.uri} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "5px 10px", color: i === 0 ? "var(--text-primary)" : "var(--text-muted)", fontStyle: suggested ? "italic" : "normal" }}>
                        {i === 0 ? _human(vendor) : ""}
                      </td>
                      <td style={{ padding: "5px 10px", color: i === 0 ? "var(--text-primary)" : "var(--text-muted)", fontStyle: suggested ? "italic" : "normal" }}>
                        {i === 0 ? _human(product) : ""}
                      </td>
                      <td style={{ padding: "5px 10px", fontFamily: "monospace", color: "var(--text-secondary)" }}>
                        {_displayVersion(p.version)}
                        {p.update && p.update !== "*" && p.update !== "-" && (
                          <span style={{ color: "var(--text-muted)" }}> + {_human(p.update)}</span>
                        )}
                      </td>
                      <td style={{ padding: "5px 10px", color: "var(--text-muted)", fontSize: 11 }}>
                        {(p.target_sw !== "*" && p.target_sw !== "-") || (p.target_hw !== "*" && p.target_hw !== "-")
                          ? [p.target_sw, p.target_hw].filter((s) => s && s !== "*" && s !== "-").map(_human).join(" / ")
                          : ""}
                      </td>
                      <td style={{ padding: "5px 10px", overflow: "visible" }}>
                        <CopyLink uri={p.uri} />
                      </td>
                    </tr>
                  ))
                )
              )}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

export default function CpesPanel({ cpes, cpesSuggested, cpesLlm }) {
  const [filter, setFilter] = useState("");

  const hasCanonical = cpes && cpes.length > 0;
  const hasSuggested = cpesSuggested && cpesSuggested.length > 0;
  const hasLlm = cpesLlm && cpesLlm.length > 0;

  if (!hasCanonical && !hasSuggested && !hasLlm) {
    return (
      <div style={{ padding: 16, color: "var(--text-muted)" }}>
        No CPEs recorded for this CVE, and not enough structured data
        in the CVE record to suggest any.
      </div>
    );
  }

  const lower = filter.trim().toLowerCase();
  const filterFn = (p) => !lower || p.uri.toLowerCase().includes(lower);

  const canonicalParsed = (cpes || []).map(parseCpe).filter(Boolean).filter(filterFn);
  const suggestedParsed = (cpesSuggested || []).map(parseCpe).filter(Boolean).filter(filterFn);
  const llmParsed = (cpesLlm || []).map(parseCpe).filter(Boolean).filter(filterFn);

  const totalParsed = (cpes || []).length + (cpesSuggested || []).length + (cpesLlm || []).length;
  const totalVisible = canonicalParsed.length + suggestedParsed.length + llmParsed.length;

  return (
    <div style={{ padding: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {totalParsed} CPE{totalParsed === 1 ? "" : "s"}
          {filter && ` (${totalVisible} match filter)`}
        </div>
        <input
          type="text"
          placeholder="Filter CPEs..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            color: "var(--text-primary)",
            padding: "4px 10px",
            borderRadius: 4,
            fontSize: 12,
            width: 220,
            fontFamily: "inherit",
          }}
        />
      </div>

      {hasCanonical && canonicalParsed.length > 0 && (
        <CpeTable list={canonicalParsed} suggested={false} />
      )}

      {hasSuggested && suggestedParsed.length > 0 && (
        <div style={{
          marginTop: hasCanonical ? 16 : 0,
          padding: 12,
          borderRadius: 6,
          border: "1px dotted var(--accent)",
          background: "rgba(88,166,255,0.04)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: "var(--accent)",
              textTransform: "uppercase", letterSpacing: 0.6,
              border: "1px dotted var(--accent)", padding: "2px 6px", borderRadius: 3,
            }}>
              Suggested
            </span>
            <span style={{ fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" }}>
              Inferred from the CVE record's affected products list. Not vendor-supplied CPE 2.3 URIs.
            </span>
          </div>
          <CpeTable list={suggestedParsed} suggested={true} />
        </div>
      )}

      {hasLlm && llmParsed.length > 0 && (
        <div style={{
          marginTop: (hasCanonical || hasSuggested) ? 16 : 0,
          padding: 12,
          borderRadius: 6,
          border: "1px dashed var(--severity-medium)",
          background: "rgba(210, 168, 255, 0.05)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: "var(--severity-medium)",
              textTransform: "uppercase", letterSpacing: 0.6,
              border: "1px dashed var(--severity-medium)", padding: "2px 6px", borderRadius: 3,
            }}>
              AI-extracted
            </span>
            <span style={{ fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" }}>
              Inferred by an LLM from the CVE description. Lowest confidence — verify before use.
            </span>
          </div>
          <CpeTable list={llmParsed} suggested={true} />
        </div>
      )}

      {totalVisible === 0 && (
        <div style={{ padding: 16, color: "var(--text-muted)", textAlign: "center" }}>
          No CPEs match the filter.
        </div>
      )}
    </div>
  );
}
